import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import { OrganizationModule } from '../types/b2b';

interface ModuleAccessResult {
  hasAccess: boolean;
  isLoading: boolean;
  modules: OrganizationModule[] | undefined;
  isTrial?: boolean;
  trialEndsAt?: string;
}

/**
 * Hook to check if the current organization has access to a specific module
 * @param moduleKey - The module key to check (e.g., 'supplier_b2b')
 * @returns Object with hasAccess boolean, loading state, and module info
 */
export const useModuleAccess = (moduleKey: string): ModuleAccessResult => {
  const { data: modules, isLoading, error } = useQuery<OrganizationModule[]>({
    queryKey: ['activeModules'],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get('/features/modules/active');
        console.log('[useModuleAccess] Active modules loaded:', response.data?.length || 0);
        return response.data;
      } catch (err) {
        // If the endpoint fails or modules are not available, return empty array
        console.error('[useModuleAccess] Failed to load modules:', err);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false, // Don't retry on failure
  });

  if (error) {
    console.error('[useModuleAccess] Query error:', error);
  }

  // Find module by key (nested in module object)
  const orgModule = modules?.find((mod) => mod.module?.key === moduleKey);
  // Module is enabled if disabledAt is null
  const hasAccess = orgModule ? orgModule.disabledAt === null : false;

  return {
    hasAccess,
    isLoading,
    modules,
    isTrial: orgModule?.isTrial,
    trialEndsAt: orgModule?.trialEndsAt ?? undefined,
  };
};

/**
 * Convenience hook for B2B/Supplier module access
 */
export const useB2BAccess = (): ModuleAccessResult => {
  return useModuleAccess('supplier_b2b');
};

/**
 * Hook to check access to multiple modules at once
 * @param moduleKeys - Array of module keys to check
 * @returns Object with access status for each module
 */
export const useMultipleModuleAccess = (
  moduleKeys: string[]
): {
  accessMap: Record<string, boolean>;
  isLoading: boolean;
  modules: OrganizationModule[] | undefined;
} => {
  const { data: modules, isLoading } = useQuery<OrganizationModule[]>({
    queryKey: ['activeModules'],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get('/features/modules/active');
        return response.data;
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const accessMap: Record<string, boolean> = {};
  moduleKeys.forEach((key) => {
    const orgModule = modules?.find((mod) => mod.module?.key === key);
    accessMap[key] = orgModule ? orgModule.disabledAt === null : false;
  });

  return {
    accessMap,
    isLoading,
    modules,
  };
};
